import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
} from "reactstrap";
import api from "../../api";
import { useAppDialog } from "./AppDialogProvider";
import { getMafiaEmpId, getMafiaEmployeeName } from "./mafiaIdentity";
import { MafiaPageHeader } from "./_shared";
import MafiaTheme from "./MafiaTheme";
import MafiaStatusTimeline from "./MafiaStatusTimeline";

export default function ReleasePart({
  partNum = "",
  rev = 0,
  revMinor = 0,
  onBack,
  onReleased,
}) {
  const dialog = useAppDialog();

  const [username, setUsername] = useState("");
  const [initials, setInitials] = useState("");
  const [history, setHistory] = useState("");
  const [rows, setRows] = useState([]);
  const [txtInitials, setTxtInitials] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pageError, setPageError] = useState("");

  const mountedRef = useRef(true);

  const styledRows = useMemo(() => {
    return (rows || []).map((row) => ({
      ...row,
      __additionalInfoClass: getAdditionalInfoClass(row),
    }));
  }, [rows]);

  const loadPage = useCallback(async () => {
    try {
      setLoading(true);
      setPageError("");

      if (!partNum) {
        setRows([]);
        setHistory("");
        setInitials("");
        return;
      }

      const currentUser = await getCurrentUsername();
      if (!mountedRef.current) return;

      setUsername(currentUser);
      setInitials(computeInitials(await getCurrentDisplayName()));

      const [oldHistory, partRows] = await Promise.all([
        getOldHistory({
          partNumber: partNum,
          rev,
          revMinor,
        }),
        getPart({
          partNumber: partNum,
          rev,
          revMinor,
        }),
      ]);

      if (!mountedRef.current) return;

      setHistory(oldHistory || "");
      setRows(partRows || []);
    } catch (error) {
      console.error("Failed to load ReleasePart:", error);
      if (mountedRef.current) {
        setPageError("Failed to load release page.");
      }
      await dialog.error("Failed to load release page.");
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [partNum, rev, revMinor, dialog]);

  useEffect(() => {
    mountedRef.current = true;
    loadPage();

    return () => {
      mountedRef.current = false;
    };
  }, [loadPage]);

  async function handleRelease() {
    let problems = "";

    for (let j = 0; j < rows.length; j++) {
      // Legacy invalid type checks intentionally preserved as inactive.
    }

    if (problems.trim() !== "") {
      const ans = await dialog.confirm({
        title: "Invalid Dimension Types",
        message:
          "There are invalid Dimension Types in this MAFIA table. Valid types include (Length, Angle, Diameter, Major Diameter, Minor Diameter, Radius, Chamfer, Fillet, Thread, Spline, Bulk Shaft, Note, GD&T, Finish). Are you sure you wish to continue? You should probably edit part before continuing.",
        confirmText: "Continue",
        cancelText: "Cancel",
        type: "warning",
      });

      if (!ans) return;
    }

    if (txtInitials.trim().toLowerCase() !== initials.trim().toLowerCase()) {
      await dialog.warning(
        `Please enter your initials of ${initials.toUpperCase()} in the box provided to continue.`
      );
      return;
    }

    try {
      setSubmitting(true);
      setPageError("");

      const success = await releasePartRequest({
        partNum,
        rev,
        revMinor,
        newHistory: `${history}; (released) ${username.toLowerCase()}`,
        oldHistory: history,
        revisedBy: username.toLowerCase(),
      });

      if (success) {
        // Go-live parity (2026-09-24): the part IS released at this point, so a
        // notification failure must not be reported as "Failed to release part".
        try {
          await sendEmailPartReleased({
            fromName: username,
            partNum,
            revMajor: rev,
            revMinor,
          });
        } catch (mailError) {
          console.error("Release part: notification e-mail failed (non-blocking)", mailError);
        }

        await dialog.success("Part Released.");

        if (onReleased) {
          onReleased({
            partNum,
            rev,
            revMinor,
          });
          return;
        }

        if (onBack) onBack();
      } else {
        setPageError("Someone else has already released this part.");
        await dialog.warning("Someone else has already released this part.");
        if (onBack) onBack();
      }
    } catch (error) {
      console.error("Release failed:", error);
      const reason = serverMessage(error, "Failed to release part.");
      setPageError(reason);
      await dialog.error(reason);
    } finally {
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }
  }

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardBody className="p-4 text-muted">Loading release workspace...</CardBody>
      </Card>
    );
  }

  return (
    <div className="release-part-page mafia-screen">
      <MafiaTheme />
      <MafiaPageHeader
        compact
        icon="ri-rocket-line"
        eyebrow="MAFIA Aux"
        title="Release Part"
        subtitle={
          <>
            <strong style={{ color: "rgba(255,255,255,0.95)" }}>{partNum || "—"}</strong>{" "}
            · Rev {rev}.{revMinor}
          </>
        }
        tone="violet"
        actions={
          <Button
            color="light"
            onClick={onBack}
            style={{
              background: "rgba(255,255,255,0.92)",
              border: "none",
              fontWeight: 500,
            }}
          >
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back
          </Button>
        }
        breadcrumb={[
          { label: "Applications", to: "/" },
          { label: "MAFIA Aux", onClick: onBack },
          { label: "Release Part" },
        ]}
      />

      <MafiaStatusTimeline history={history} current="releasing" />

      <Row className="g-4">
        <Col xl={12}>
          <Card className="border-0 shadow-sm">
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-3">
              <div className="release-part-toolbar-main">
                <div className="release-part-toolbar-main-left">
                  {/* Back moved to page header */}
                </div>

                <div className="release-part-toolbar-main-right">
                  <InputGroup className="release-part-initials-group">
                    <InputGroupText className="bg-light border-end-0">
                      <i className="ri-user-line" />
                    </InputGroupText>
                    <Input
                      id="txtInitials"
                      type="text"
                      value={txtInitials}
                      onChange={(e) => setTxtInitials(e.target.value)}
                      className="border-start-0"
                      placeholder={initials ? `Enter ${initials.toUpperCase()} to release` : "Enter initials"}
                      disabled={submitting}
                    />
                  </InputGroup>
                </div>
              </div>

              <div className="release-part-toolbar-utility">
                <Button
                  color="success"
                  className="release-part-action-btn release-part-utility-btn"
                  onClick={handleRelease}
                  disabled={submitting || !partNum}
                >
                  <i className="ri-check-double-line release-part-btn-icon me-1" />
                  {submitting ? "Releasing..." : "Release Part"}
                </Button>

                <Badge color="light" className="release-part-info-badge">
                  User: {username || "—"}
                </Badge>

                <div className="release-part-info-pill">
                  <span className="release-part-info-label">Part</span>
                  <span className="release-part-info-value" title={partNum || "—"}>
                    {partNum || "—"}
                  </span>
                </div>

                <div className="release-part-info-pill">
                  <span className="release-part-info-label">Revision</span>
                  <span className="release-part-info-value">
                    {rev}.{revMinor}
                  </span>
                </div>

                <div className="release-part-info-pill">
                  <span className="release-part-info-label">Rows Loaded</span>
                  <span className="release-part-info-value">{styledRows.length}</span>
                </div>

                <div className="release-part-refresh-note">
                  Release with initials <strong>{initials || "—"}</strong>
                </div>
              </div>

              {pageError ? (
                <div className="release-part-error-banner mt-3">
                  <i className="ri-error-warning-line me-2" />
                  {pageError}
                </div>
              ) : null}
            </CardHeader>

            <CardBody className="p-4">
              <div className="release-part-table-shell">
                <div className="release-part-table-scroll">
                  <Table responsive={false} className="align-middle mb-0 release-part-table">
                    <thead className="table-light">
                      <tr>
                        <HeaderCell label="DNA" />
                        <HeaderCell label="Dimension Type" />
                        <HeaderCell label="Unit" />
                        <HeaderCell label="Nominal" />
                        <HeaderCell label="Min" />
                        <HeaderCell label="Max" />
                        <HeaderCell label="Tolerance Class" />
                        <HeaderCell label="In Process" />
                        <HeaderCell label="Method Of Recording" />
                        <HeaderCell label="Special Instructions" />
                        <HeaderCell label="WI or ATP Number" />
                        <HeaderCell label="Gages" />
                        <HeaderCell label="Additional Info" />
                        <HeaderCell label="Mom Op #" />
                      </tr>
                    </thead>
                    <tbody>
                      {styledRows.length === 0 ? (
                        <tr>
                          <td colSpan={14} className="text-center py-5 text-muted">
                            {pageError ? "Unable to load part data." : "No data found."}
                          </td>
                        </tr>
                      ) : (
                        styledRows.map((row, index) => (
                          <tr key={`${row.DNA}-${index}`} className="release-part-data-row">
                            <Cell value={row.DNA} />
                            <Cell value={row.DimensionType} />
                            <Cell value={row.Unit} />
                            <Cell value={row.Nominal} />
                            <Cell value={row.Min} />
                            <Cell value={row.Max} />
                            <Cell value={row.ToleranceClass} />
                            <Cell value={row.InProcess} />
                            <Cell value={row.MethodOfRecording} />
                            <Cell value={row.SpecialInstructions} />
                            <Cell value={row.WIATPNumber} />
                            <Cell value={row.Gages} />
                            <Cell value={row.AdditionalInfo} className={row.__additionalInfoClass} />
                            <Cell value={row.MOMOpNumber} />
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <style>{`
        .release-part-toolbar-main {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 0.9rem;
        }

        .release-part-toolbar-main-left {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .release-part-toolbar-main-right {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          flex: 0 0 360px;
          min-width: 280px;
        }

        .release-part-toolbar-utility {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          padding-top: 0.25rem;
        }

        .release-part-initials-group {
          width: 100%;
        }

        .release-part-action-btn {
            white-space: nowrap;
          }
          /* Make disabled primary buttons obviously disabled (not faded primary) */
          .release-part-action-btn:disabled,
          .release-part-action-btn.disabled {
            background-color: #e9ebec !important;
            border-color: #e9ebec !important;
            color: #878a99 !important;
            opacity: 1 !important;
          }


        .release-part-utility-btn {
            min-width: 110px;
          }

        .release-part-btn-icon {
          font-size: 0.95rem;
        }

        .release-part-info-badge {
          display: inline-flex;
          align-items: center;
          min-height: 38px;
          border: 1px solid #e9ebec;
          padding: 0.58rem 0.8rem;
          border-radius: 0.65rem;
          font-size: 0.85rem;
          color: #212529;
          background: #f8fafc;
        }

        .release-part-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

        .release-part-info-label {
            font-size: 0.7rem;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            color: #878a99;
            white-space: nowrap;
          }

        .release-part-info-value {
            font-size: 0.8125rem;
            font-weight: 600;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

        .release-part-refresh-note {
          min-height: 38px;
          display: inline-flex;
          align-items: center;
          padding: 0.45rem 0.2rem;
          font-size: 0.84rem;
          color: #878a99;
          font-weight: 500;
        }

        .release-part-error-banner {
          display: flex;
          align-items: center;
          padding: 0.8rem 0.95rem;
          border-radius: 0.8rem;
          border: 1px solid rgba(220, 53, 69, 0.18);
          background: rgba(220, 53, 69, 0.06);
          color: #842029;
          font-weight: 500;
        }

        .btn-soft-dark {
          background: #eff2f7;
          color: #212529;
          border: 1px solid #e9ebec;
        }

        .btn-soft-dark:hover,
        .btn-soft-dark:focus {
          background: #e9ebec;
          color: #212529;
          border-color: #dee2e6;
        }

        .release-part-table-shell {
          border: 1px solid #e9ebec;
          border-radius: 1rem;
          background: #fff;
        }

        .release-part-table-scroll {
          max-height: calc(100vh - 260px);
          min-height: 320px;
          overflow-x: auto;
          overflow-y: auto;
        }

        .release-part-table {
          min-width: 1700px;
        }

        .release-part-table thead th {
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f3f6f9;
          white-space: nowrap;
        }

        .release-part-table tbody td {
          padding: 0.9rem 0.9rem;
          border-top: 1px solid #eef2f7;
          color: #495057;
          vertical-align: top;
        }

        .release-part-data-row:nth-of-type(even) {
          background: #fbfcfe;
        }

        .release-part-data-row:hover {
          background: #f5f9ff;
        }

        .release-part-additional-reference {
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          font-size: 13px;
        }

        .release-part-additional-note {
          font-family: inherit;
        }

        .release-part-additional-gdt {
          font-weight: 700;
          letter-spacing: 0.04em;
        }

        .release-part-additional-default {
          font-family: Georgia, serif;
        }

        @media (max-width: 1199.98px) {
          .release-part-toolbar-main {
            flex-direction: column;
            align-items: stretch;
          }

          .release-part-toolbar-main-right {
            flex: 1 1 auto;
            min-width: 100%;
          }
        }

        @media (max-width: 991.98px) {
          .release-part-info-pill {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            min-height: 32px;
            padding: 0.3rem 0.65rem;
            border: 1px solid #e9ebec;
            border-radius: 0.375rem;
            background: #f3f6f9;
            max-width: 300px;
          }

          .release-part-toolbar-utility {
            align-items: stretch;
          }
        }
      `}</style>
    </div>
  );
}

function HeaderCell({ label }) {
  return <th>{label}</th>;
}

function Cell({ value, className = "" }) {
  return <td className={className}>{value ?? ""}</td>;
}

function computeInitials(displayName) {
  // Round 16/17: derive initials from the human display name (see ConfirmPart).
  const name = (displayName || "").trim();
  if (!name) return "";
  const parts = name.split(/[\s._\-,]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return parts.length === 1 ? parts[0].substring(0, 2).toUpperCase() : "";
}

function getAdditionalInfoClass(row) {
  const dimensionType = String(row.DimensionType || "").toLowerCase().trim();
  const toleranceClass = String(row.ToleranceClass || "").toLowerCase().trim();

  if (toleranceClass.includes("reference")) {
    return "release-part-additional-reference";
  }

  if (
    dimensionType.includes("note") ||
    dimensionType.includes("*") ||
    dimensionType.includes("finish")
  ) {
    return "release-part-additional-note";
  }

  if (dimensionType.includes("gd&t")) {
    return "release-part-additional-gdt";
  }

  return "release-part-additional-default";
}

async function getCurrentUsername() {
  return getMafiaEmpId();
}

async function getCurrentDisplayName() {
  const name = await getMafiaEmployeeName();
  return name || "";
}

async function getOldHistory(request) {
  const response = await api.post("mafia/old-history", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });

  return response.data;
}

async function getPart(request) {
  const response = await api.post("mafia/part", {
    partNumber: request.partNumber,
    rev: request.rev,
    revMinor: request.revMinor,
  });

  return (response.data || []).map((row) => ({
    DNA: row.dna,
    DimensionType: row.dimensionType,
    Unit: row.unit,
    Nominal: row.nominal,
    Min: row.min,
    Max: row.max,
    ToleranceClass: row.toleranceClass,
    InProcess: row.inProcess,
    MethodOfRecording: row.methodOfRecording,
    SpecialInstructions: row.specialInstructions,
    WIATPNumber: row.wiatpNumber,
    Gages: row.gages,
    AdditionalInfo: row.additionalInfo,
    MOMOpNumber: row.momOpNumber,
  }));
}

async function releasePartRequest(request) {
  const response = await api.post("mafia/release-part", {
    partNum: request.partNum,
    rev: request.rev,
    revMinor: request.revMinor,
    newHistory: request.newHistory,
    oldHistory: request.oldHistory,
    revisedBy: request.revisedBy,
  });

  return response.data;
}

async function sendEmailPartReleased(request) {
  const response = await api.post("mafia/email-part-released", {
    fromName: request.fromName,
    partNum: request.partNum,
    revMajor: request.revMajor,
    revMinor: request.revMinor,
  });

  return response.data;
}

// Go-live parity (2026-09-24): show the server's reason (e.g. "You may not
// confirm this part, as you were the last one to modify it", "already being
// checked by ...", a 403 tier message) instead of a generic failure text.
function serverMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim() !== "") return data.trim();
  const msg = data?.error || data?.message || data?.title;
  if (typeof msg === "string" && msg.trim() !== "") return msg.trim();
  return fallback;
}